import {
   ChangelogReversibility,
   ColorEnum,
   PullbackFailureReason,
   PullbackScreenStatus
} from '../graphqlTypes'

// Fixtures and scenario table for the Application Pullback mock
// (docs/features/application-pullback). The scenario is chosen in the UI by
// picking a transition: every scenario stage id starts with `mock-` so it can
// never collide with a real stage id.

export type MockRecord = Record<string, unknown>

export interface MockContext {
   requestId: string
   applicationId: string
}

export type MockError = (context: MockContext) => MockRecord

export interface ChangeLogStep {
   status: PullbackScreenStatus
   failureReason?: PullbackFailureReason
   withList?: boolean
}

export interface PullbackScenario {
   label: string
   color?: ColorEnum
   initiateError?: MockError
   changeLogError?: MockError
   isBlocked?: boolean
   approveError?: MockError
   rejectError?: MockError
   statusAfterDecisionError?: ChangeLogStep
   approveStatus?: PullbackScreenStatus
   afterApprove?: ChangeLogStep[]
   networkFailuresAfterApprove?: number
}

export const mockError = (
   typename: string,
   fields: MockRecord = {}
): MockError => (context: MockContext) => ({
   __typename: typename,
   message: `Mock ${typename}`,
   applicationId: context.applicationId,
   requestId: context.requestId,
   ...fields
})

// ─── Change-log fixtures ─────────────────────────────────────────────────────

const mockChange = (fields: MockRecord): MockRecord => ({
   __typename: 'Change',
   type: ChangelogReversibility.Reversible,
   isOptional: false,
   performedByName: 'Asha Rao',
   doneAt: '2026-09-20 10:15:00',
   actionName: null,
   consequence: null,
   blockedReason: null,
   ...fields
})

const mockGroup = (
   moduleKey: string,
   moduleDisplayName: string,
   changes: MockRecord[]
): MockRecord => ({
   __typename: 'ChangeModuleGroup',
   moduleKey,
   moduleDisplayName,
   changes
})

export const buildApprovableGroups = (): MockRecord[] => [
   mockGroup('documents', 'Documents', [
      mockChange({
         changeId: 'chg-doc-001',
         changeName: 'Approval letter generated',
         subjectName: 'Approval Letter.pdf',
         actionName: 'Revoke Approval Letter',
         consequence: 'The approval letter will be withdrawn.'
      }),
      mockChange({
         changeId: 'chg-doc-002',
         isOptional: true,
         changeName: 'Site plan published',
         subjectName: 'Site Plan v3.pdf',
         doneAt: '2026-09-20 10:20:00',
         actionName: 'Unpublish Site Plan',
         consequence: 'The published site plan will be hidden.'
      })
   ]),
   mockGroup('application_stage', 'Application stage', [
      mockChange({
         changeId: 'chg-stage-001',
         changeName: 'Stage changed',
         subjectName: 'Residential Pipeline \u2014 Scrutiny',
         actionName: 'Move back to Scrutiny',
         consequence: 'The application returns to Scrutiny.'
      }),
      mockChange({
         changeId: 'chg-stage-002',
         isOptional: true,
         changeName: 'Reviewer assigned',
         subjectName: '',
         doneAt: null,
         actionName: null,
         consequence: null
      })
   ]),
   mockGroup('payments', 'Payments', [
      mockChange({
         changeId: 'chg-pay-001',
         changeName: 'Fee assessment created',
         subjectName: 'Assessment #A-1029',
         performedByName: 'Ravi Kumar',
         doneAt: '2026-09-20 11:00:00',
         actionName: 'Cancel Fee Assessment',
         consequence: 'The fee assessment will be cancelled.'
      }),
      mockChange({
         changeId: 'chg-pay-info-001',
         type: ChangelogReversibility.Informational,
         changeName: 'Payment receipt emailed',
         subjectName: 'Receipt #R-5531',
         performedByName: 'System',
         doneAt: '2026-09-20 11:05:00'
      })
   ])
]

export const buildBlockedGroups = (): MockRecord[] => [
   mockGroup('documents', 'Documents', [
      mockChange({
         changeId: 'chg-doc-001',
         changeName: 'Approval letter generated',
         subjectName: 'Approval Letter.pdf',
         actionName: 'Revoke Approval Letter',
         consequence: 'The approval letter will be withdrawn.'
      })
   ]),
   mockGroup('payments', 'Payments', [
      mockChange({
         changeId: 'chg-pay-blocked-001',
         type: ChangelogReversibility.Irreversible,
         changeName: 'Payment settled',
         subjectName: 'Payment #P-7781',
         performedByName: 'Ravi Kumar',
         doneAt: '2026-09-20 11:30:00',
         blockedReason:
            'The payment was settled with the bank and cannot be undone.'
      })
   ])
]

// ─── Scenarios (keyed by toStageId, shown as transitions) ────────────────────

const RUNNING: ChangeLogStep = { status: PullbackScreenStatus.Running }

const decisionScenario = (
   label: string,
   error: MockError,
   scope: 'BOTH' | 'APPROVE' = 'BOTH'
): PullbackScenario => ({
   label,
   approveError: error,
   rejectError: scope === 'BOTH' ? error : undefined
})

const initiateScenario = (
   label: string,
   error: MockError
): PullbackScenario => ({ label, initiateError: error })

const changeLogScenario = (
   label: string,
   error: MockError
): PullbackScenario => ({ label, changeLogError: error })

export const HAPPY_AFTER_APPROVE: ChangeLogStep[] = [
   RUNNING,
   RUNNING,
   RUNNING,
   { status: PullbackScreenStatus.Succeeded }
]

export const PULLBACK_SCENARIOS: Record<string, PullbackScenario> = {
   'mock-happy': {
      label: 'Mock: completes (RUNNING then SUCCEEDED)',
      color: ColorEnum.Green,
      afterApprove: HAPPY_AFTER_APPROVE
   },
   'mock-network-blip': {
      label: 'Mock: 2 network read failures after approve, recovers on its own',
      color: ColorEnum.Orange,
      networkFailuresAfterApprove: 2,
      afterApprove: HAPPY_AFTER_APPROVE
   },
   'mock-network-lost': {
      label:
         'Mock: 4 network read failures after approve, Connection lost + Retry',
      networkFailuresAfterApprove: 4,
      afterApprove: HAPPY_AFTER_APPROVE
   },
   'mock-blocked': {
      label: 'Mock: BLOCKED before approve (irreversible change)',
      color: ColorEnum.Rose,
      isBlocked: true
   },
   'mock-late-blocked': {
      label: 'Mock: BLOCKED after approve',
      afterApprove: [
         RUNNING,
         RUNNING,
         { status: PullbackScreenStatus.Blocked, withList: true }
      ]
   },
   'mock-failed-invalidated': {
      label: 'Mock: FAILED + INVALIDATED after approve',
      afterApprove: [
         RUNNING,
         RUNNING,
         {
            status: PullbackScreenStatus.Failed,
            failureReason: PullbackFailureReason.Invalidated
         }
      ]
   },
   'mock-failed-error': {
      label: 'Mock: approve returns FAILED, then FAILED + ERROR',
      approveStatus: PullbackScreenStatus.Failed,
      afterApprove: [
         {
            status: PullbackScreenStatus.Failed,
            failureReason: PullbackFailureReason.Error
         }
      ]
   },
   'mock-timed-out': {
      ...decisionScenario(
         'Mock: TIMED_OUT status (decision says not awaiting)',
         mockError('PullbackRequestIsNotAwaitingApproval', {
            currentStatus: PullbackScreenStatus.TimedOut
         })
      ),
      statusAfterDecisionError: { status: PullbackScreenStatus.TimedOut }
   },
   'mock-init-application-not-found': initiateScenario(
      'Mock initiate: ApplicationNotFound',
      mockError('ApplicationNotFound')
   ),
   'mock-init-access-denied': initiateScenario(
      'Mock initiate: ApplicationManagementAccessDenied',
      mockError('ApplicationManagementAccessDenied', { userId: 'mock-user' })
   ),
   'mock-init-not-on-a-stage': initiateScenario(
      'Mock initiate: ApplicationNotOnAStage',
      mockError('ApplicationNotOnAStage')
   ),
   'mock-init-stage-not-configured': initiateScenario(
      'Mock initiate: StageNotConfiguredForPullback',
      mockError('StageNotConfiguredForPullback', {
         fromStageId: 'mock-from',
         toStageId: 'mock-init-stage-not-configured'
      })
   ),
   'mock-init-no-role': initiateScenario(
      'Mock initiate: NoRoleOnPullbackTransition',
      mockError('NoRoleOnPullbackTransition', {
         fromStageId: 'mock-from',
         toStageId: 'mock-init-no-role'
      })
   ),
   'mock-init-remarks-required': initiateScenario(
      'Mock initiate: PullbackRemarksRequired',
      mockError('PullbackRemarksRequired')
   ),
   'mock-init-already-in-flight': initiateScenario(
      'Mock initiate: PullbackAlreadyInFlight',
      mockError('PullbackAlreadyInFlight')
   ),
   'mock-init-attachments-required': initiateScenario(
      'Mock initiate: PullbackAttachmentsRequired',
      mockError('PullbackAttachmentsRequired')
   ),
   'mock-init-attachments-exceed': initiateScenario(
      'Mock initiate: PullbackAttachmentsExceedLimit',
      mockError('PullbackAttachmentsExceedLimit', { maxAttachments: 5 })
   ),
   'mock-init-content-type': initiateScenario(
      'Mock initiate: PullbackAttachmentContentTypeNotAllowed',
      mockError('PullbackAttachmentContentTypeNotAllowed', {
         fileName: 'notes.docx',
         contentType: 'application/msword',
         allowedContentTypes: ['application/pdf', 'image/jpeg', 'image/png']
      })
   ),
   'mock-init-too-large': initiateScenario(
      'Mock initiate: PullbackAttachmentTooLarge',
      mockError('PullbackAttachmentTooLarge', {
         fileName: 'scan.pdf',
         sizeInBytes: 12582912,
         limitInBytes: 10485760
      })
   ),
   'mock-decision-application-not-found': decisionScenario(
      'Mock decision: ApplicationNotFound',
      mockError('ApplicationNotFound')
   ),
   'mock-decision-access-denied': decisionScenario(
      'Mock decision: ApplicationManagementAccessDenied',
      mockError('ApplicationManagementAccessDenied', { userId: 'mock-user' })
   ),
   'mock-decision-request-not-found': decisionScenario(
      'Mock decision: PullbackRequestNotFound',
      mockError('PullbackRequestNotFound')
   ),
   'mock-decision-run-expired': decisionScenario(
      'Mock decision: PullbackRunExpired',
      mockError('PullbackRunExpired')
   ),
   'mock-approve-required-missing': decisionScenario(
      'Mock approve: RequiredChangesAreNotApproved (with a null id)',
      mockError('RequiredChangesAreNotApproved', {
         missingChangeIds: ['chg-doc-001', null]
      }),
      'APPROVE'
   ),
   'mock-approve-not-in-log': decisionScenario(
      'Mock approve: ChangesAreNotInChangeLog (with a null id)',
      mockError('ChangesAreNotInChangeLog', {
         unknownChangeIds: ['chg-unknown', null]
      }),
      'APPROVE'
   ),
   'mock-approve-duplicate': decisionScenario(
      'Mock approve: DuplicateChangelogId',
      mockError('DuplicateChangelogId', {
         moduleDisplayName: 'Documents',
         changeId: 'chg-doc-001'
      }),
      'APPROVE'
   ),
   'mock-changelog-application-not-found': changeLogScenario(
      'Mock change log: ApplicationNotFound',
      mockError('ApplicationNotFound')
   ),
   'mock-changelog-access-denied': changeLogScenario(
      'Mock change log: ApplicationManagementAccessDenied',
      mockError('ApplicationManagementAccessDenied', { userId: 'mock-user' })
   ),
   'mock-changelog-request-not-found': changeLogScenario(
      'Mock change log: PullbackRequestNotFound',
      mockError('PullbackRequestNotFound')
   ),
   'mock-changelog-duplicate': changeLogScenario(
      'Mock change log: DuplicateChangelogId',
      mockError('DuplicateChangelogId', {
         moduleDisplayName: 'Payments',
         changeId: 'chg-pay-001'
      })
   )
}
